/**
 * @vuelto-surface
 * mobile: ok
 * desktop: n/a
 * strategy: mobile-only
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ticketColors, ticketFonts } from '../ticket/public-api';

type Props = {
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  productCount: number;
  loading: boolean;
  hasMore: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onJumpToPage?: (page: number) => void;
  variant?: 'pages' | 'compact';
};

export function MobilePagination({
  pageIndex,
  pageSize,
  totalCount,
  productCount,
  loading,
  hasMore,
  onPreviousPage,
  onNextPage,
  onJumpToPage,
  variant = 'pages',
}: Props) {
  if (totalCount === 0) return null;

  const pageNum = pageIndex + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = pageIndex * pageSize + 1;
  const to = Math.min(totalCount, pageIndex * pageSize + productCount);
  const rangeLabel = productCount === 0
    ? `Página ${pageNum} de ${totalPages} · ${totalCount} en total`
    : `${from}–${to} · ${totalCount}`;

  const prevDisabled = pageIndex === 0 || loading;
  const nextDisabled = !hasMore || loading;

  if (variant === 'compact') {
    return (
      <View style={styles.wrap}>
        <Text style={styles.infoCompact}>
          {loading ? 'Cargando…' : rangeLabel}
        </Text>
        <View style={styles.navBtns}>
          <TouchableOpacity
            style={[styles.compactBtn, prevDisabled && styles.pagBtnDisabled]}
            onPress={onPreviousPage}
            disabled={prevDisabled}
            activeOpacity={0.55}
            accessibilityRole="button"
            accessibilityLabel="Página anterior"
          >
            <Text style={styles.compactBtnText}>Ant</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.compactBtn, nextDisabled && styles.pagBtnDisabled]}
            onPress={onNextPage}
            disabled={nextDisabled}
            activeOpacity={0.55}
            accessibilityRole="button"
            accessibilityLabel="Página siguiente"
          >
            <Text style={styles.compactBtnText}>Sig</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const visiblePages = getVisiblePages(pageNum, totalPages);

  return (
    <View style={styles.wrap}>
      <Text style={styles.info}>
        {loading ? 'Cargando…' : productCount === 0
          ? `Página ${pageNum} de ${totalPages} · ${totalCount} en total`
          : `Mostrando ${from}–${to} de ${totalCount}`}
      </Text>
      <View style={styles.navBtns}>
        <TouchableOpacity
          style={[styles.pagBtn, prevDisabled && styles.pagBtnDisabled]}
          onPress={onPreviousPage}
          disabled={prevDisabled}
          activeOpacity={0.55}
          accessibilityRole="button"
          accessibilityLabel="Página anterior"
        >
          <Text style={styles.pagBtnText}>‹</Text>
        </TouchableOpacity>
        {visiblePages.map((p) => (
          <TouchableOpacity
            key={p}
            style={[styles.pagBtn, p === pageNum && styles.pagBtnActive]}
            onPress={() => {
              if (p === pageNum) return;
              if (onJumpToPage) {
                onJumpToPage(p - 1);
              } else if (p < pageNum) {
                onPreviousPage();
              } else {
                onNextPage();
              }
            }}
            disabled={p === pageNum}
            activeOpacity={0.55}
            accessibilityRole="button"
            accessibilityLabel={`Página ${p}`}
            accessibilityState={{ selected: p === pageNum }}
          >
            <Text style={[styles.pagBtnText, p === pageNum && styles.pagBtnTextActive]}>{p}</Text>
          </TouchableOpacity>
        ))}
        <TouchableOpacity
          style={[styles.pagBtn, nextDisabled && styles.pagBtnDisabled]}
          onPress={onNextPage}
          disabled={nextDisabled}
          activeOpacity={0.55}
          accessibilityRole="button"
          accessibilityLabel="Página siguiente"
        >
          <Text style={styles.pagBtnText}>›</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function getVisiblePages(current: number, total: number): number[] {
  if (total <= 3) return Array.from({ length: total }, (_, i) => i + 1);
  if (current === 1) return [1, 2, 3];
  if (current === total) return [total - 2, total - 1, total];
  return [current - 1, current, current + 1];
}

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 4,
    marginTop: 4,
  },
  info: {
    fontSize: 13,
    color: ticketColors.ink3,
    fontWeight: '500',
  },
  infoCompact: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontWeight: '500',
    fontFamily: ticketFonts.mono,
    fontVariant: ['tabular-nums'],
  },
  compactBtn: {
    height: 32,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  compactBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  navBtns: {
    flexDirection: 'row',
    gap: 6,
  },
  pagBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pagBtnActive: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  pagBtnDisabled: {
    opacity: 0.3,
  },
  pagBtnText: {
    fontSize: 14,
    color: ticketColors.ink2,
  },
  pagBtnTextActive: {
    color: ticketColors.goldInk,
    fontWeight: '700',
  },
});
